import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  getFreeListenData,
  canListenForFree,
  recordFreeListen,
  getRemainingFreeListens,
  hasReachedFreeListenLimit,
  hasUsedAnyFreeListens,
  getListenedMeditationIds,
  resetFreeListenData
} from '../utils/cookieManager';

interface FreeListenContextType {
  freeListenCount: number;
  listenedMeditationIds: string[];
  hasUsedFreeListen: boolean;
  canListen: (meditationId: string) => boolean;
  recordListen: (meditationId: string) => void;
  getRemainingListens: () => number;
  hasReachedLimit: () => boolean;
  resetListens: () => void;
}

const FreeListenContext = createContext<FreeListenContextType | undefined>(undefined);

interface FreeListenProviderProps {
  children: ReactNode;
}

export function FreeListenProvider({ children }: FreeListenProviderProps) {
  const [freeListenCount, setFreeListenCount] = useState(0);
  const [listenedMeditationIds, setListenedMeditationIds] = useState<string[]>([]);
  const [hasUsedFreeListen, setHasUsedFreeListen] = useState(false);

  // Load initial data from cookie
  useEffect(() => {
    const data = getFreeListenData();
    setFreeListenCount(data.count);
    setListenedMeditationIds(data.meditationIds);
    setHasUsedFreeListen(hasUsedAnyFreeListens());
  }, []);

  const canListen = (meditationId: string): boolean => {
    return canListenForFree(meditationId);
  };

  const recordListen = (meditationId: string): void => {
    recordFreeListen(meditationId);

    // Update state
    const data = getFreeListenData();
    setFreeListenCount(data.count);
    setListenedMeditationIds(data.meditationIds);
    setHasUsedFreeListen(true);
  };

  const getRemainingListens = (): number => {
    return getRemainingFreeListens();
  };

  const hasReachedLimit = (): boolean => {
    return hasReachedFreeListenLimit();
  };

  const resetListens = (): void => {
    resetFreeListenData();
    setFreeListenCount(0);
    setListenedMeditationIds([]);
    setHasUsedFreeListen(false);
  };

  const value: FreeListenContextType = {
    freeListenCount,
    listenedMeditationIds,
    hasUsedFreeListen,
    canListen,
    recordListen,
    getRemainingListens,
    hasReachedLimit,
    resetListens
  };

  return (
    <FreeListenContext.Provider value={value}>
      {children}
    </FreeListenContext.Provider>
  );
}

export function useFreeListens(): FreeListenContextType {
  const context = useContext(FreeListenContext);
  if (context === undefined) {
    throw new Error('useFreeListens must be used within a FreeListenProvider');
  }
  return context;
}
